"use client";

import { AlertTriangle, Check, ShoppingCart } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

interface AddToCartDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title?: string;
	description?: string;
	actionText?: string; // e.g. "View Cart" or "Checkout"
	actionHref?: string;
	showPrimaryAction?: boolean;
	variant?: "success" | "error";
	detailsTitle?: string;
	details?: string[];
	secondaryActionText?: string;
	onContinueShopping?: () => void;
	supportActionText?: string;
	supportActionHref?: string;
}

export function AddToCartDialog({
	open,
	onOpenChange,
	title = "Added to Cart!",
	description = "Your item has been successfully added to your cart.",
	actionText = "View Cart",
	actionHref = "/cart",
	showPrimaryAction = true,
	variant = "success",
	detailsTitle = "Limited stock items",
	details = [],
	secondaryActionText = "Continue Shopping",
	onContinueShopping,
	supportActionText,
	supportActionHref,
}: AddToCartDialogProps) {
	const isError = variant === "error";
	const uniqueDetails = Array.from(
		new Set(details.map((entry) => entry.trim()).filter(Boolean)),
	);
	const iconContainerClass = isError ? "bg-red-100" : "bg-emerald-100";
	const iconClass = isError ? "text-red-600" : "text-emerald-600";
	const titleClass = isError ? "text-red-700 dark:text-red-400" : "";

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md p-0 gap-0 max-h-[90dvh] overflow-hidden">
				<div className="max-h-[90dvh] overflow-y-auto px-4 pb-4 pt-5 sm:px-6 sm:pb-6 sm:pt-6">
					<DialogHeader className="flex flex-col items-center justify-center space-y-4 pt-2">
						<div className={`rounded-full p-3 ${iconContainerClass}`}>
							{isError ? (
								<AlertTriangle className={`h-6 w-6 ${iconClass}`} />
							) : (
								<Check className={`h-6 w-6 ${iconClass}`} />
							)}
						</div>
						<div className="text-center space-y-2">
							<DialogTitle
								className={`text-xl sm:text-2xl font-bold text-center ${titleClass}`}
							>
								{title}
							</DialogTitle>
							<DialogDescription className="text-center text-sm sm:text-base leading-relaxed">
								{description}
							</DialogDescription>
						</div>
					</DialogHeader>
					{uniqueDetails.length > 0 && (
						<div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50/70 dark:bg-red-950/20 p-3 mt-4">
							<p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
								{detailsTitle}
							</p>
							<ul className="space-y-1 list-disc pl-5 max-h-48 sm:max-h-64 overflow-y-auto pr-1">
								{uniqueDetails.map((meal) => (
									<li
										key={meal}
										className="text-sm leading-relaxed text-red-700/90 dark:text-red-300"
									>
										{meal}
									</li>
								))}
							</ul>
						</div>
					)}
					<div className="flex flex-col gap-3 pt-4">
						{showPrimaryAction && (
							<Button
								className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold gap-2"
								asChild
							>
								<Link
									href={actionHref}
									onClick={() => onOpenChange(false)}
									className="w-full"
								>
									<ShoppingCart className="w-5 h-5" />
									{actionText}
								</Link>
							</Button>
						)}
						{supportActionText && supportActionHref && (
							<Button
								variant="link"
								className="w-full h-auto p-0 text-sm leading-relaxed text-center whitespace-normal"
								asChild
							>
								<Link
									href={supportActionHref}
									onClick={() => onOpenChange(false)}
								>
									{supportActionText}
								</Link>
							</Button>
						)}
						<Button
							variant="outline"
							className="w-full h-11 sm:h-12 text-sm sm:text-base font-semibold"
							onClick={() => {
								onOpenChange(false);
								onContinueShopping?.();
							}}
						>
							{secondaryActionText}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
